import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  InputAdornment
} from '@mui/material';
import { formatDate } from '../utils/format';
import { ensureLogin, fetchProjects, fetchScheduleTasks, updateScheduleTask, deleteScheduleTask } from '../utils/api';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';

// Removido: dados simulados. Usaremos API real (Projects, Users, Activities).

const Activities = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [taskEdits, setTaskEdits] = useState({});
  const [editingTaskId, setEditingTaskId] = useState(null);

  const toDateInputValue = (dateString) => {
    if (!dateString) return '';
    try {
      const d = new Date(dateString);
      const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString();
      return iso.slice(0, 10);
    } catch {
      return '';
    }
  };

  useEffect(() => {
    const init = async () => {
      await ensureLogin();
      const proj = await fetchProjects();
      setProjects(proj);
      if (proj && proj.length > 0) {
        setSelectedProjectId(String(proj[0].id));
      }
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    const loadTasks = async () => {
      if (!selectedProjectId) { setTasks([]); return; }
      const list = await fetchScheduleTasks(parseInt(selectedProjectId, 10));
      const normalized = Array.isArray(list) ? list.map(t => ({
        ...t,
        startDate: toDateInputValue(t.startDate) || '',
        endDate: toDateInputValue(t.endDate) || '',
        progress: Number.isFinite(t.progress) ? t.progress : 0,
        status: t.status || ((Number.isFinite(t.progress) && parseInt(t.progress) >= 100) ? 'concluido' : (Number.isFinite(t.progress) && parseInt(t.progress) > 0 ? 'em_execucao' : 'planeado')),
        tecnicoOperacoesText: t.tecnicoOperacoesText || '',
        coordenadorTecnicoText: t.coordenadorTecnicoText || '',
        formadorConsultorText: t.formadorConsultorText || ''
      })) : [];
      setTasks(normalized);
      setTaskEdits({});
    };
    loadTasks();
  }, [selectedProjectId]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

  const handleProjectSelect = (e) => setSelectedProjectId(e.target.value);

  const handleEditField = (id, field, value) => {
    setTaskEdits(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value }
    }));
  };

  const handleSaveTask = async (task) => {
    const edit = taskEdits[task.id] || {};
    const payload = {
      name: edit.name ?? task.name ?? '',
      responsible: edit.responsible ?? task.responsible ?? '',
      startDate: edit.startDate ?? task.startDate ?? null,
      endDate: edit.endDate ?? task.endDate ?? null,
      progress: Number.isFinite(edit.progress) ? Math.max(0, Math.min(100, parseInt(edit.progress))) : (Number.isFinite(task.progress) ? task.progress : 0),
      status: edit.status ?? task.status ?? ((Number.isFinite(edit.progress ?? task.progress) && parseInt(edit.progress ?? task.progress) >= 100) ? 'concluido' : (Number.isFinite(edit.progress ?? task.progress) && parseInt(edit.progress ?? task.progress) > 0 ? 'em_execucao' : 'planeado')),
      tecnicoOperacoesText: edit.tecnicoOperacoesText ?? task.tecnicoOperacoesText ?? null,
      coordenadorTecnicoText: edit.coordenadorTecnicoText ?? task.coordenadorTecnicoText ?? null,
      formadorConsultorText: edit.formadorConsultorText ?? task.formadorConsultorText ?? null
    };
    await ensureLogin();
    await updateScheduleTask(parseInt(selectedProjectId, 10), task.id, payload);
    const refreshed = await fetchScheduleTasks(parseInt(selectedProjectId, 10));
    const normalized = Array.isArray(refreshed) ? refreshed.map(t => ({
      ...t,
      startDate: toDateInputValue(t.startDate) || '',
      endDate: toDateInputValue(t.endDate) || '',
      progress: Number.isFinite(t.progress) ? t.progress : 0,
      status: t.status || ((Number.isFinite(t.progress) && parseInt(t.progress) >= 100) ? 'concluido' : (Number.isFinite(t.progress) && parseInt(t.progress) > 0 ? 'em_execucao' : 'planeado')),
      tecnicoOperacoesText: t.tecnicoOperacoesText || '',
      coordenadorTecnicoText: t.coordenadorTecnicoText || '',
      formadorConsultorText: t.formadorConsultorText || ''
    })) : [];
    setTasks(normalized);
    setTaskEdits(prev => ({ ...prev, [task.id]: {} }));
    setEditingTaskId(null);
  };

  const handleDeleteTask = async (task) => {
    if (!window.confirm('Tem certeza que deseja excluir esta tarefa?')) return;
    await ensureLogin();
    await deleteScheduleTask(parseInt(selectedProjectId, 10), task.id);
    const refreshed = await fetchScheduleTasks(parseInt(selectedProjectId, 10));
    const normalized = Array.isArray(refreshed) ? refreshed.map(t => ({
      ...t,
      startDate: toDateInputValue(t.startDate) || '',
      endDate: toDateInputValue(t.endDate) || '',
      progress: Number.isFinite(t.progress) ? t.progress : 0
    })) : [];
    setTasks(normalized);
  };

  // Removido: criação de nova tarefa. Página apenas lista/edita tarefas existentes.

  const formatDateStr = (dateString) => {
    if (!dateString) return '';
    return formatDate(dateString);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>A carregar tarefas…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="div" gutterBottom>
        Gestão de Tarefas
      </Typography>
      {/* Removido o botão geral de edição. A edição agora é ativada por linha. */}
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Projeto</InputLabel>
              <Select value={selectedProjectId} label="Projeto" onChange={(e) => setSelectedProjectId(e.target.value)}>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={String(project.id)}>{project.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="tabela de tarefas">
          <TableHead>
            <TableRow>
              <TableCell>Tarefa</TableCell>
              <TableCell>Responsável</TableCell>
              <TableCell>Início</TableCell>
              <TableCell>Fim</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Técnico de Operações</TableCell>
              <TableCell>Coordenador Técnico</TableCell>
              <TableCell>Formador/Consultor</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((t) => (
                <TableRow key={t.id}>
                  <TableCell>
                    <TextField 
                      fullWidth 
                      disabled={editingTaskId !== t.id} 
                      value={t.name ?? ''} 
                      onChange={(e) => handleEditField(t.id, 'name', e.target.value)} 
                      InputProps={{
                        endAdornment: t?.id ? (
                          <InputAdornment position="end">
                            <Typography variant="caption" color="text.secondary">ID: {t.id}</Typography>
                          </InputAdornment>
                        ) : null
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField fullWidth disabled={editingTaskId !== t.id} value={t.responsible ?? ''} onChange={(e) => handleEditField(t.id, 'responsible', e.target.value)} />
                  </TableCell>
                  <TableCell>
                    <TextField fullWidth disabled={editingTaskId !== t.id} type="date" InputLabelProps={{ shrink: true }} value={t.startDate ?? ''} onChange={(e) => handleEditField(t.id, 'startDate', e.target.value)} />
                  </TableCell>
                  <TableCell>
                    <TextField fullWidth disabled={editingTaskId !== t.id} type="date" InputLabelProps={{ shrink: true }} value={t.endDate ?? ''} onChange={(e) => handleEditField(t.id, 'endDate', e.target.value)} />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      disabled={editingTaskId !== t.id}
                      select
                      label="Estado"
                      value={t.status || ((t.progress ?? 0) >= 100 ? 'concluido' : ((t.progress ?? 0) > 0 ? 'em_execucao' : 'planeado'))}
                      onChange={(e) => {
                        const val = e.target.value;
                        const prog = val === 'concluido' ? 100 : (val === 'em_execucao' ? (Number.isFinite(t.progress) && t.progress > 0 && t.progress < 100 ? t.progress : 10) : 0);
                        handleEditField(t.id, 'status', val);
                        handleEditField(t.id, 'progress', prog);
                      }}
                    >
                      <MenuItem value="planeado">Planeado</MenuItem>
                      <MenuItem value="em_execucao">Em Execução</MenuItem>
                      <MenuItem value="concluido">Concluído</MenuItem>
                      <MenuItem value="cancelado">Cancelado</MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField fullWidth disabled={editingTaskId !== t.id} value={t.tecnicoOperacoesText ?? ''} onChange={(e) => handleEditField(t.id, 'tecnicoOperacoesText', e.target.value)} placeholder="Nome do técnico (texto livre)" />
                  </TableCell>
                  <TableCell>
                    <TextField fullWidth disabled={editingTaskId !== t.id} value={t.coordenadorTecnicoText ?? ''} onChange={(e) => handleEditField(t.id, 'coordenadorTecnicoText', e.target.value)} placeholder="Nome do coordenador (texto livre)" />
                  </TableCell>
                  <TableCell>
                    <TextField fullWidth disabled={editingTaskId !== t.id} value={t.formadorConsultorText ?? ''} onChange={(e) => handleEditField(t.id, 'formadorConsultorText', e.target.value)} placeholder="Nome do formador/consultor (texto livre)" />
                  </TableCell>
                  <TableCell align="center">
                    {editingTaskId === t.id ? (
                      <>
                        <IconButton color="primary" onClick={() => handleSaveTask(t)} title="Guardar"><SaveIcon /></IconButton>
                        <IconButton color="default" onClick={() => { setTaskEdits(prev => ({ ...prev, [t.id]: {} })); setEditingTaskId(null); }} title="Cancelar"><CloseIcon /></IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton color="primary" onClick={() => setEditingTaskId(t.id)} title="Editar"><EditIcon /></IconButton>
                        <IconButton color="error" onClick={() => handleDeleteTask(t)} title="Eliminar"><DeleteIcon /></IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            {tasks.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body1" sx={{ py: 2 }}>
                    Nenhuma tarefa encontrada para o projeto selecionado
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={tasks.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>
      
      {/* Diálogo removido: tarefas são editáveis inline na tabela */}
    </Box>
  );
};

export default Activities;